import { cartAmountsReducer } from "~/features/addToCart/redux/cartAmounts/reducer";
import { srpReducer } from "~/features/search/redux/reducer";
import { cartPageReducer } from "~/features/cart/cart/redux/reducers/cartPageReducer";
import { checkoutPageReducer } from "~/features/cart/cart/redux/reducers/checkoutPageReducer";
import { cartReducer } from "~/features/cart/cart/redux/reducers/cartReducer";
import { globalMessagesReducer } from "~/features/toasts/redux/reducer";
import { productsReducer } from "~/domains/product/redux/reducer";
import { contractsReducer } from "~/features/contracts/redux/reducer";
import { productDetailsPageReducer } from "~/features/productDetails/redux/productDetailsReducer";
import navigationReducer from "~/features/navigation/redux/navigationSlice";
import { linearConfiguratorReducer } from "~/features/configurator/Linear/redux/reducer";
import { externalContentReducer } from "~/features/externalContent/redux/reducer";
import wishlistUIReducer from "~/features/wishlist/redux/wishlistSlice";
import cartAlternatesReducer from "~/features/cart/cartV2/redux/cartAlternatesSlice";

import { regionSelectionBannerReducer } from "~/features/regionSelection/redux/reducer";
import { HOUSING_SA_REDUCER } from "~/features/selectionAssistant/housing/redux/reducer";
import { notificationsReducer } from "~/features/notifications/redux/NotificationsReducer";
import { saReducer } from "~/features/selectionAssistant/bearing/redux/reducer";
import { minicartReducer } from "~/features/cart/minicart/minicartRedux";
import { affiliateReducer } from "~/domains/affiliate/redux/reducer";
import { searchQueryReducer } from "~/features/search/redux/autosuggest/reducer";
import { navigationBreadcrumbsReducer } from "~/features/breadcrumbs/redux/reducer";
import type { Action, Reducer, ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import userPreferencesReducer from "~/domains/userPreferences/redux/userPreferencesSlice";
import { baseApi } from "~/app/store/baseApi";
import { authOverlayReducer } from "~/features/registration/components/redux/AuthReducer";
import { ACCOUNT_CONTEXT_SWITCH_SUCCEEDED } from "~/common/accountContextTransition";
import { affiliateSessionMiddleware } from "~/app/store/affiliateSessionMiddleware";
import sessionExpirationReducer from "~/features/auth/session/sessionExpirationSlice";

const combinedRootReducer = combineReducers({
  cartPage: cartPageReducer,
  checkoutPage: checkoutPageReducer,
  cart: cartReducer,
  globalMessages: globalMessagesReducer,
  products: productsReducer,
  cartAmounts: cartAmountsReducer,
  linearConfigurator: linearConfiguratorReducer,
  regionSelectionBanner: regionSelectionBannerReducer,
  navigationBreadcrumbs: navigationBreadcrumbsReducer,

  contracts: contractsReducer,
  wishlistUI: wishlistUIReducer,

  srp: srpReducer,
  pdp: productDetailsPageReducer,
  sa: saReducer,
  navigation: navigationReducer,

  externalContent: externalContentReducer,
  housingSA: HOUSING_SA_REDUCER,
  userPreferences: userPreferencesReducer,

  notifications: notificationsReducer,

  minicart: minicartReducer,
  cartAlternates: cartAlternatesReducer,

  affiliate: affiliateReducer,
  authOverlay: authOverlayReducer,

  search: searchQueryReducer,
  sessionExpiration: sessionExpirationReducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

export type RootState = ReturnType<typeof combinedRootReducer>;

/**
 * Account identity is a server-state boundary. Reset only legacy Redux projections populated from
 * account-sensitive endpoints. Local interaction state and user-scoped preferences must survive;
 * TanStack Query and RTK Query caches are reset separately by the transition coordinator.
 */
export const rootReducer: Reducer<RootState, UnknownAction, Partial<RootState>> = (state, action) => {
  if (action.type !== ACCOUNT_CONTEXT_SWITCH_SUCCEEDED || !state) {
    return combinedRootReducer(state as RootState | undefined, action);
  }

  return combinedRootReducer(
    {
      ...state,
      cartPage: undefined,
      checkoutPage: undefined,
      cart: undefined,
      products: undefined,
      cartAmounts: undefined,
      srp: {
        ...state.srp,
        meta: undefined,
        results: undefined,
        content: undefined,
        facets: undefined,
        config: undefined,
        productsOnSale: undefined,
        recommendationPlacement: undefined,
        sort: undefined,
        seriesTree: undefined,
      },
      pdp: undefined,
      navigation: undefined,
      navigationBreadcrumbs: undefined,
      externalContent: undefined,
      housingSA: undefined,
      minicart: undefined,
      affiliate: undefined,
    } as unknown as RootState,
    action,
  );
};

export function setupStore(preloadedState?: Partial<RootState>) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(baseApi.middleware, affiliateSessionMiddleware),
  });
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, undefined, Action<string>>;
